import { z } from 'zod';
import { CandidatesConst, CurrencyMap } from '@axiom/const';
import { PasswordStrengthUtil } from '@axiom/utils';

export const SchemaTypes = {
  ZodArray: 'SchemaArray',
  ZodBoolean: 'SchemaBoolean',
  ZodNumber: 'SchemaNumber',
  ZodObject: 'SchemaObject',
  ZodRecord: 'SchemaRecord',
  ZodString: 'SchemaString',
  ZodEnum: 'SchemaEnum',
  // Generated DTO schemas emit `z.nativeEnum(...)` for enum fields; treat it as
  // an enum field so generated schemas are directly usable in Form components.
  ZodNativeEnum: 'SchemaEnum',
  ZodUnion: 'SchemaUnion',
} as const;

export const CustomSchemaTypes = {
  SchemaBadges: 'SchemaBadges',
  SchemaBlob: 'SchemaBlob',
  SchemaCurrency: 'SchemaCurrency',
  SchemaDate: 'SchemaDate',
  SchemaEmail: 'SchemaEmail',
  SchemaJpeg: 'SchemaJpeg',
  SchemaLocation: 'SchemaLocation',
  SchemaMonthYear: 'SchemaMonthYear',
  SchemaPassword: 'SchemaPassword',
  SchemaPdf: 'SchemaPdf',
  SchemaPhoneNumber: 'SchemaPhoneNumber',
  SchemaPng: 'SchemaPng',
  SchemaRate: 'SchemaRate',
  SchemaTime: 'SchemaTime',
  SchemaTimeOfDay: 'SchemaTimeOfDay',
  SchemaTimestamp: 'SchemaTimestamp',
  SchemaWordDoc: 'SchemaWordDoc',
  SchemaYear: 'SchemaYear',
} as const;

/**
 * Custom Types
 */

// The `const` modifier makes T infer as the literal union of an inline array
// argument (e.g. ZodArrayToEnum(['A', 'B']) => 'A' | 'B') instead of widening
// to `string`; callers that already pass a precisely-typed array are
// unaffected, since inference then just reads off that type as before.
export const ZodArrayToEnum = <const T>(arr: T[]): [T, ...T[]] =>
  arr as [T, ...T[]];

/**
 * Unwraps nullable and/or optional schemas to make them required.
 * Handles both .nullable() and .nullish() schemas by unwrapping all wrapper layers.
 *
 * @example
 * const dataSchema = z.object({
 *   email: z.string().email().nullish(),
 *   name: z.string().nullable()
 * });
 *
 * const formSchema = dataSchema.extend({
 *   email: makeRequired(dataSchema.shape.email), // required email
 *   name: makeRequired(dataSchema.shape.name)    // required name
 * });
 */
export function makeRequired<T extends z.ZodTypeAny>(schema: T): z.ZodTypeAny {
  let current: z.ZodTypeAny = schema;
  // Keep unwrapping until we hit the base type
  while (current instanceof z.ZodOptional || current instanceof z.ZodNullable) {
    current = current.unwrap();
  }
  return current;
}

export const SchemaPassword = z
  .string()
  .max(255)
  .refine(password => PasswordStrengthUtil.meetsRequirement(password), {
    message: 'Your password is not strong enough.',
  });
(SchemaPassword as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaPassword;

export const SchemaDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
(SchemaDate as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaDate;

export const SchemaMonthYear = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
(SchemaMonthYear as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaMonthYear;

export const SchemaTime = z.string().regex(/^\d{2}:\d{2}:\d{2}-\d{4}$/);
(SchemaTime as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaTime;

export const SchemaTimeOfDay = z.string().regex(/^\d{2}:\d{2}:\d{2}$/);
(SchemaTimeOfDay as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaTimeOfDay;

export const SchemaTimestamp = z
  .string()
  .regex(
    /\d{4}-[01]\d-[0-3]\dT[0-2](?:\d:[0-5]){2}\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/
  );

(SchemaTimestamp as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaTimestamp;

export const SchemaYear = z.number().int().positive();
(SchemaYear as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaYear;

export const SchemaEmail = z
  .string()
  .trim()
  .email()
  .max(255)
  .transform(val => val.toLowerCase());
(SchemaEmail as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaEmail;

/**
 * Here we check for digit count and non-approved-special characters
 * That was good enough for now (per TPM)
 * This isn't 100% for every pattern since we don't have that kinda time.
 * */
export const SchemaPhoneNumber = z
  .string()
  .trim()
  .refine(
    phoneNumber => {
      const approveSpecialCharacters = new Set([' ', '(', ')', '-', '+', 'x']);
      const numBits = [...phoneNumber];
      const { numberCount, characters } = numBits.reduce<{
        numberCount: number;
        characters: string[];
      }>(
        (crt, bit) => {
          if (Number.parseInt(bit, 10) >= 0) {
            crt.numberCount++;
            return crt;
          }

          crt.characters.push(bit);
          return crt;
        },
        { numberCount: 0, characters: [] }
      );

      let isGood = numberCount >= 7 && numberCount <= 17;
      if (isGood && characters.length > 0) {
        isGood = characters.every(character =>
          approveSpecialCharacters.has(character.toLowerCase())
        );
      }

      return isGood;
    },
    {
      message:
        'Phone numbers must be 7-21 characters and can include only numbers, spaces, and these special characters: +, -, (, ), x.',
    }
  );
(SchemaPhoneNumber as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaPhoneNumber;

export const SchemaLocation = z.object({
  locationAddressComponents: z
    .object({
      addresses: z
        .array(
          z.object({
            long_name: z.string(),
            short_name: z.string(),
            types: z.array(z.string()),
          })
        )
        .nullish(),
    })
    .nullish(),
  locationLatitude: z.number().nullish(),
  locationLongitude: z.number().nullish(),
  locationName: z.string().nullish(),
  locationPlaceId: z.string().nullish(),
});
(SchemaLocation as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaLocation;
export type Location = z.infer<typeof SchemaLocation>;

export const SchemaRate = z
  .string()
  .trim()
  .refine(
    value => {
      const bits = value.split(/[,.]/);

      const invalid = bits.some((bit, index, ary) => {
        let isInvalid = /(.*\D.*)/.test(bit);

        if (!isInvalid) {
          if (ary.length > 2) {
            if (index === 0) {
              isInvalid = bit.length === 0 || bit.length > 3;
            } else if (index === ary.length - 1) {
              isInvalid = bit.length !== 2;
            } else {
              isInvalid = bit.length !== 3;
            }
          } else if (ary.length > 1) {
            if (index === 1) {
              isInvalid = bit.length === 0;
            } else if (index === ary.length - 1) {
              isInvalid = bit.length !== 2;
            }
          }
        }

        return isInvalid;
      });

      return !invalid;
    },
    { message: 'This can only contain numbers, commas, and periods.' }
  );
(SchemaRate as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaRate;

const allowedCurrencies = Object.values(CurrencyMap.codes);

export const SchemaCurrency = z.enum(ZodArrayToEnum(allowedCurrencies));
(SchemaCurrency as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaCurrency;

const BADGES = Object.keys(CandidatesConst.Badges) as Array<
  keyof typeof CandidatesConst.Badges
>;
export const SchemaBadges = z.array(z.enum(ZodArrayToEnum(BADGES)));
(SchemaBadges as ZodExtendType<unknown>)._customType =
  CustomSchemaTypes.SchemaBadges;

const buildFileSchema = (customType: string, fileTypes: string[]) => {
  const schema = z
    .instanceof(File)
    .refine(file => fileTypes.includes(file.type), {
      message: 'Invalid file type',
    });
  (schema as unknown as ZodExtendType<unknown>)._customType = customType;
  (schema as unknown as ZodExtendType<unknown>)._customData = { fileTypes };
  return schema;
};

export const SchemaJpeg = buildFileSchema(CustomSchemaTypes.SchemaJpeg, [
  'image/jpeg',
  'image/jpg',
]);

export const SchemaPng = buildFileSchema(CustomSchemaTypes.SchemaPng, [
  'image/png',
]);

export const SchemaPdf = buildFileSchema(CustomSchemaTypes.SchemaPdf, [
  'application/pdf',
]);

export const SchemaWordDoc = buildFileSchema(CustomSchemaTypes.SchemaWordDoc, [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const buildBlobSchema = (customType: string, fileTypes: string[]) => {
  const schema = z
    .instanceof(Blob)
    .refine(blob => fileTypes.includes(blob.type), {
      message: 'Invalid file type',
    });
  (schema as unknown as ZodExtendType<unknown>)._customType = customType;
  (schema as unknown as ZodExtendType<unknown>)._customData = { fileTypes };
  return schema;
};

export const SchemaBlob = buildBlobSchema(CustomSchemaTypes.SchemaBlob, [
  'image/jpeg',
  'image/jpg',
  'image/png',
]);
